import { useState, useEffect, useRef, useMemo } from "react"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { 
  ArrowRight, 
  Layers, 
  FlaskConical, 
  Shirt, 
  Cog, 
  Pill, 
  Building2, 
  Cpu, 
  Wheat, 
  Package,
  Wrench,
  MapPin,
  ExternalLink,
} from "lucide-react"
import { Link } from "@tanstack/react-router"

const TRADEMAP_SOURCE_URL = "https://legacy.trademap.org/Country_SelProductCountry.aspx"

interface ProductShare {
  name: string
  sharePercent: number
  icon: typeof Layers
  color: string
  valueUsd: number
  growthPercent: number
  metricValue: number
  points: number[]
}

interface StateSupplyData {
  id: string
  stateName: string
  hubName: string
  suppliers: number
  demandLevel: "high" | "medium" | "low"
  coordinates: [number, number] // [lat, lng]
  demandZoneRadiusMeters: number
  demandColor: string
  topProducts: ProductShare[]
}

const STATE_DATA: StateSupplyData[] = [
  {
    id: "gujarat",
    stateName: "Gujarat",
    hubName: "Surat",
    suppliers: 142,
    demandLevel: "high",
    coordinates: [21.1702, 72.8311],
    demandZoneRadiusMeters: 175000,
    demandColor: "#be001e",
    topProducts: [
      { name: "Chemicals", sharePercent: 32, icon: FlaskConical, color: "#0284c7", valueUsd: 6.87, growthPercent: 12.6, metricValue: 6870, points: [5400, 2900, 4800, 4200, 6100, 5100, 7400, 6870] },
      { name: "Textiles", sharePercent: 24, icon: Shirt, color: "#be001e", valueUsd: 5.12, growthPercent: 9.8, metricValue: 5120, points: [4600, 3100, 4900, 3800, 5300, 4400, 5600, 5120] },
      { name: "Machinery", sharePercent: 18, icon: Cog, color: "#059669", valueUsd: 3.85, growthPercent: 15.4, metricValue: 3850, points: [3100, 1950, 3400, 2700, 3950, 3200, 4200, 3850] },
      { name: "Pharmaceuticals", sharePercent: 14, icon: Pill, color: "#7c3aed", valueUsd: 2.94, growthPercent: -4.2, metricValue: 2940, points: [3400, 3800, 2600, 3200, 2400, 3100, 2200, 2940] },
      { name: "Ceramics", sharePercent: 12, icon: Building2, color: "#d97706", valueUsd: 2.10, growthPercent: 18.5, metricValue: 2100, points: [1500, 950, 1750, 1300, 2200, 1650, 2450, 2100] },
    ],
  },
  {
    id: "delhi-ncr",
    stateName: "Delhi NCR",
    hubName: "Delhi NCR",
    suppliers: 176,
    demandLevel: "high",
    coordinates: [28.6139, 77.2090],
    demandZoneRadiusMeters: 140000,
    demandColor: "#be001e",
    topProducts: [
      { name: "Readymade Garments", sharePercent: 30, icon: Shirt, color: "#be001e", valueUsd: 6.42, growthPercent: 14.2, metricValue: 6420, points: [5200, 3100, 4900, 4100, 5900, 4800, 6800, 6420] },
      { name: "CleanTech & Solar", sharePercent: 22, icon: Cpu, color: "#059669", valueUsd: 4.70, growthPercent: 26.5, metricValue: 4700, points: [3300, 2100, 3800, 2900, 4400, 3600, 5100, 4700] },
      { name: "Paper & Packaging", sharePercent: 18, icon: Package, color: "#0284c7", valueUsd: 3.80, growthPercent: 8.4, metricValue: 3800, points: [3200, 2200, 3600, 2900, 4100, 3300, 4250, 3800] },
      { name: "Auto Wiring", sharePercent: 16, icon: Wrench, color: "#7c3aed", valueUsd: 3.25, growthPercent: 16.8, metricValue: 3250, points: [2500, 1650, 2900, 2250, 3400, 2700, 3600, 3250] },
      { name: "Electronics", sharePercent: 14, icon: Cog, color: "#d97706", valueUsd: 2.90, growthPercent: -3.5, metricValue: 2900, points: [3300, 3650, 2700, 3250, 2500, 3100, 2400, 2900] },
    ],
  },
  {
    id: "maharashtra",
    stateName: "Maharashtra",
    hubName: "Pune",
    suppliers: 43,
    demandLevel: "medium",
    coordinates: [18.5204, 73.8567],
    demandZoneRadiusMeters: 170000,
    demandColor: "#f59e0b",
    topProducts: [
      { name: "Auto Components", sharePercent: 34, icon: Cog, color: "#be001e", valueUsd: 7.60, growthPercent: 17.5, metricValue: 7600, points: [6100, 3800, 5900, 4900, 7100, 5800, 8200, 7600] },
      { name: "Precision Forging", sharePercent: 26, icon: Wrench, color: "#0284c7", valueUsd: 5.45, growthPercent: 11.2, metricValue: 5450, points: [4500, 2900, 4800, 3900, 5600, 4600, 6100, 5450] },
      { name: "Pharmaceuticals", sharePercent: 18, icon: Pill, color: "#059669", valueUsd: 3.90, growthPercent: 12.8, metricValue: 3900, points: [3100, 2100, 3600, 2800, 4100, 3300, 4350, 3900] },
      { name: "Polymers & Plastics", sharePercent: 12, icon: FlaskConical, color: "#7c3aed", valueUsd: 2.65, growthPercent: -4.8, metricValue: 2650, points: [3100, 3450, 2550, 3050, 2350, 2900, 2250, 2650] },
      { name: "Electrical Equipment", sharePercent: 10, icon: Cpu, color: "#d97706", valueUsd: 2.15, growthPercent: 15.1, metricValue: 2150, points: [1650, 1100, 1950, 1450, 2300, 1750, 2500, 2150] },
    ],
  },
  {
    id: "west-bengal",
    stateName: "West Bengal",
    hubName: "Kolkata",
    suppliers: 136,
    demandLevel: "medium",
    coordinates: [22.5726, 88.3639],
    demandZoneRadiusMeters: 150000,
    demandColor: "#f59e0b",
    topProducts: [
      { name: "Jute & Agro-Bags", sharePercent: 35, icon: Wheat, color: "#059669", valueUsd: 4.90, growthPercent: 9.6, metricValue: 4900, points: [4100, 2700, 4400, 3600, 5100, 4200, 5500, 4900] },
      { name: "Iron & Pipe Fittings", sharePercent: 25, icon: Wrench, color: "#be001e", valueUsd: 3.55, growthPercent: 15.2, metricValue: 3550, points: [2800, 1850, 3200, 2500, 3700, 3050, 4000, 3550] },
      { name: "Finished Leather", sharePercent: 18, icon: Package, color: "#d97706", valueUsd: 2.60, growthPercent: -5.4, metricValue: 2600, points: [3000, 3300, 2450, 2950, 2250, 2800, 2200, 2600] },
      { name: "Tea & Spices", sharePercent: 12, icon: FlaskConical, color: "#0284c7", valueUsd: 1.85, growthPercent: 8.2, metricValue: 1850, points: [1550, 1050, 1700, 1300, 2050, 1600, 2200, 1850] },
      { name: "Railway Equipment", sharePercent: 10, icon: Cog, color: "#7c3aed", valueUsd: 1.45, growthPercent: 21.0, metricValue: 1450, points: [1050, 680, 1250, 950, 1550, 1200, 1700, 1450] },
    ],
  },
  {
    id: "tamil-nadu",
    stateName: "Tamil Nadu",
    hubName: "Tirupur",
    suppliers: 209,
    demandLevel: "high",
    coordinates: [11.1085, 77.3411],
    demandZoneRadiusMeters: 165000,
    demandColor: "#10b981",
    topProducts: [
      { name: "Cotton Knitwear", sharePercent: 36, icon: Shirt, color: "#be001e", valueUsd: 6.87, growthPercent: 12.5, metricValue: 6870, points: [5500, 3000, 5100, 4300, 6200, 5200, 7500, 6870] },
      { name: "Automotive Castings", sharePercent: 24, icon: Cog, color: "#0284c7", valueUsd: 4.60, growthPercent: 14.8, metricValue: 4600, points: [3700, 2350, 4100, 3300, 4800, 3950, 5200, 4600] },
      { name: "Technical Yarns", sharePercent: 16, icon: Layers, color: "#059669", valueUsd: 3.10, growthPercent: -3.8, metricValue: 3100, points: [3500, 3850, 2900, 3450, 2750, 3300, 2600, 3100] },
      { name: "Footwear & Leather", sharePercent: 14, icon: Package, color: "#7c3aed", valueUsd: 2.75, growthPercent: 19.4, metricValue: 2750, points: [2050, 1350, 2400, 1850, 2900, 2300, 3150, 2750] },
      { name: "EV & Electronics", sharePercent: 10, icon: Cpu, color: "#d97706", valueUsd: 2.05, growthPercent: 31.2, metricValue: 2050, points: [1350, 850, 1650, 1200, 2150, 1650, 2400, 2050] },
    ],
  },
]

const GRAPH_W = 300
const GRAPH_H = 132
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug"]

export function ProductSnapshotGraph({
  series,
  className,
}: {
  series: {
    title: string
    currentValue: number
    growthPercent: number
    points: number[]
  }
  className?: string
}) {
  const [hover, setHover] = useState<number | null>(null)

  const data = useMemo(() => series?.points ?? [0], [series])
  const maxVal = useMemo(() => Math.max(...data, 1) * 1.08, [data])
  const minVal = useMemo(() => Math.min(...data), [data])
  const floorVal = useMemo(() => Math.max(0, minVal * 0.52), [minVal])
  const valRange = useMemo(() => Math.max(1, maxVal - floorVal), [maxVal, floorVal])

  const activeIdx = hover == null ? data.length - 1 : hover
  const activeVal = data[activeIdx] ?? series.currentValue

  const firstVal = data[0] || 1
  const deltaVal = activeVal - firstVal
  const deltaPct = ((deltaVal / firstVal) * 100).toFixed(1)
  const isPositive = deltaVal >= 0

  const plotTop = 14
  const plotBottom = 110
  const plotHeight = plotBottom - plotTop
  const slotWidth = (GRAPH_W - 16) / data.length
  const barWidth = Math.max(14, Math.min(22, slotWidth * 0.58))

  const linePath = useMemo(() => {
    return data
      .map((val, i) => {
        const slotLeft = 8 + i * slotWidth
        const slotCenter = slotLeft + slotWidth / 2
        const barH = Math.max(12, ((val - floorVal) / valRange) * plotHeight)
        const barY = plotBottom - barH
        return `${i ? "L" : "M"} ${slotCenter.toFixed(1)} ${barY.toFixed(1)}`
      })
      .join(" ")
  }, [data, floorVal, valRange, plotHeight, plotBottom, slotWidth])

  return (
    <div className={`rounded-xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/70 p-2.5 sm:p-3 flex flex-col justify-between h-full select-none ${className || ""}`}>
      {/* Top Header */}
      <div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs sm:text-[13px] font-bold text-slate-800 dark:text-slate-200 truncate">
            {series.title}
          </span>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-400 dark:text-slate-500 shrink-0 font-medium">
            <a
              href={TRADEMAP_SOURCE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 hover:text-primary transition-colors"
              title="Data Source: Trade Map"
            >
              <span>Source</span>
              <ExternalLink size={9} className="opacity-70" />
            </a>
          </div>
        </div>

        {/* Big Number & Delta with Active Month */}
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-xl sm:text-2xl font-black font-mono tracking-tight text-slate-900 dark:text-white tabular-nums">
            {activeVal.toLocaleString()}
          </span>
          <span className="text-[10.5px] font-bold text-slate-500 dark:text-slate-400">
            USD $M
          </span>
        </div>
        <div className="text-[11px] font-semibold tabular-nums mt-0.5 flex items-center gap-1.5">
          <span
            style={{
              color: isPositive ? "var(--graph-spark-pos, #059669)" : "var(--graph-spark-neg, #e11d48)"
            }}
          >
            {isPositive ? `+${deltaVal}` : deltaVal} ({isPositive ? `+${deltaPct}%` : `${deltaPct}%`})
          </span>
          <span className="text-slate-400 dark:text-slate-500 text-[10px] font-normal">
            · {MONTHS[activeIdx % MONTHS.length]}
          </span>
        </div>
      </div>

      {/* SVG Bar Graph */}
      <div className="relative w-full h-[115px] sm:h-[128px] mt-1">
        <svg
          viewBox={`0 0 ${GRAPH_W} ${GRAPH_H}`}
          preserveAspectRatio="none"
          className="w-full h-full overflow-visible"
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            {/* Positive Bar Gradient */}
            <linearGradient id="snapshotBarPosGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--graph-bar-pos-to, #34d399)" stopOpacity="0.95" />
              <stop offset="100%" stopColor="var(--graph-bar-pos-from, #059669)" stopOpacity="0.5" />
            </linearGradient>

            {/* Negative Bar Gradient */}
            <linearGradient id="snapshotBarNegGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--graph-bar-neg-to, #fb7185)" stopOpacity="0.95" />
              <stop offset="100%" stopColor="var(--graph-bar-neg-from, #be001e)" stopOpacity="0.5" />
            </linearGradient>

            {/* Trend Spline Gradient */}
            <linearGradient id="snapshotLinePosGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="var(--graph-line-pos, #059669)" />
              <stop offset="100%" stopColor="var(--graph-bar-pos-to, #34d399)" />
            </linearGradient>
            <linearGradient id="snapshotLineNegGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="var(--graph-line-neg, #e11d48)" />
              <stop offset="100%" stopColor="var(--graph-bar-neg-to, #fb7185)" />
            </linearGradient>
          </defs>

          {/* Subtle horizontal guideline lines */}
          <line
            x1="8"
            y1={plotTop + plotHeight * 0.3}
            x2={GRAPH_W - 8}
            y2={plotTop + plotHeight * 0.3}
            stroke="currentColor"
            strokeDasharray="3 3"
            strokeWidth="0.75"
            className="text-slate-200 dark:text-slate-800"
          />
          <line
            x1="8"
            y1={plotTop + plotHeight * 0.65}
            x2={GRAPH_W - 8}
            y2={plotTop + plotHeight * 0.65}
            stroke="currentColor"
            strokeDasharray="3 3"
            strokeWidth="0.75"
            className="text-slate-200 dark:text-slate-800"
          />

          {/* Baseline axis */}
          <line
            x1="8"
            y1={plotBottom}
            x2={GRAPH_W - 8}
            y2={plotBottom}
            stroke="currentColor"
            strokeWidth="1"
            className="text-slate-200 dark:text-slate-800"
          />

          {/* 1. Underlying Bars with Modern Gradients */}
          {data.map((val, i) => {
            const slotLeft = 8 + i * slotWidth
            const barX = slotLeft + (slotWidth - barWidth) / 2
            const barH = Math.max(12, ((val - floorVal) / valRange) * plotHeight)
            const barY = plotBottom - barH
            const isActive = i === activeIdx
            const barFill = isPositive ? "url(#snapshotBarPosGrad)" : "url(#snapshotBarNegGrad)"

            return (
              <rect
                key={`bar-${i}`}
                x={barX}
                y={barY}
                width={barWidth}
                height={barH}
                rx="4"
                fill={barFill}
                className="transition-all duration-200"
                style={{
                  opacity: isActive ? 1 : hover === null ? 0.75 : 0.38,
                  filter: isActive
                    ? `drop-shadow(0 -2px 6px ${isPositive ? "var(--graph-spark-pos-glow, rgba(5,150,105,0.35))" : "var(--graph-spark-neg-glow, rgba(225,29,72,0.35))"})`
                    : "none",
                }}
              />
            )
          })}

          {/* 2. Trend Line Passing Through Top of Bars */}
          <path
            d={linePath}
            fill="none"
            stroke={isPositive ? "url(#snapshotLinePosGrad)" : "url(#snapshotLineNegGrad)"}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transition-all duration-200"
            style={{
              filter: `drop-shadow(0 2px 4px ${isPositive ? "var(--graph-spark-pos-glow, rgba(5,150,105,0.25))" : "var(--graph-spark-neg-glow, rgba(225,29,72,0.25))"})`
            }}
          />

          {/* 3. Node Dots, Active Value Badges, Months, & Hit Areas */}
          {data.map((val, i) => {
            const slotLeft = 8 + i * slotWidth
            const slotCenter = slotLeft + slotWidth / 2
            const barH = Math.max(12, ((val - floorVal) / valRange) * plotHeight)
            const barY = plotBottom - barH
            const isActive = i === activeIdx
            const monthLabel = MONTHS[i % MONTHS.length]
            const pointColor = isPositive ? "var(--graph-node-pos, #047857)" : "var(--graph-node-neg, #be001e)"
            const pointGlow = isPositive ? "var(--graph-spark-pos-glow, rgba(5,150,105,0.4))" : "var(--graph-spark-neg-glow, rgba(225,29,72,0.4))"

            return (
              <g
                key={`col-${i}`}
                className="cursor-pointer"
                onMouseEnter={() => setHover(i)}
              >
                {/* Full-column invisible hit area for easy hover on desktop/touch */}
                <rect
                  x={slotLeft}
                  y={plotTop}
                  width={slotWidth}
                  height={plotBottom - plotTop + 22}
                  fill="transparent"
                />

                {/* Node dot on the line right at top-center of each bar */}
                <circle
                  cx={slotCenter}
                  cy={barY}
                  r={isActive ? 4.5 : 3}
                  fill={isActive ? pointColor : "white"}
                  stroke={pointColor}
                  strokeWidth={isActive ? 2.5 : 1.75}
                  className="transition-all duration-150"
                  style={{
                    filter: isActive
                      ? `drop-shadow(0 2px 6px ${pointGlow})`
                      : "none",
                  }}
                />

                {/* Active value badge directly above the bar and dot */}
                {isActive && (
                  <text
                    x={slotCenter}
                    y={barY - 7}
                    textAnchor="middle"
                    className="text-[9.5px] font-mono font-bold fill-slate-900 dark:fill-white"
                  >
                    {val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val}
                  </text>
                )}

                {/* Month Label along bottom */}
                <text
                  x={slotCenter}
                  y={GRAPH_H - 4}
                  textAnchor="middle"
                  className={`text-[9.5px] transition-colors select-none ${
                    isActive
                      ? "fill-primary font-bold text-[10px]"
                      : "fill-slate-400 dark:fill-slate-500 font-medium"
                  }`}
                >
                  {monthLabel}
                </text>
              </g>
            )
          })}
        </svg>

        {/* Hover Tooltip Pill */}
        {hover !== null && (
          <div
            className="absolute pointer-events-none -top-1.5 transform -translate-x-1/2 -translate-y-full bg-slate-900/95 text-white dark:bg-white/95 dark:text-slate-900 px-2 py-0.5 rounded text-[10px] font-mono font-bold shadow-md whitespace-nowrap z-20"
            style={{
              left: `${((8 + activeIdx * slotWidth + slotWidth / 2) / GRAPH_W) * 100}%`,
            }}
          >
            {MONTHS[activeIdx % MONTHS.length]}: ${activeVal.toLocaleString()}M
          </div>
        )}
      </div>
    </div>
  )
}

export function PanIndiaNetwork() {
  const [selectedStateId, setSelectedStateId] = useState<string>("delhi-ncr")
  const activeState = STATE_DATA.find((s) => s.id === selectedStateId) || STATE_DATA[0]

  const [selectedProductIdx, setSelectedProductIdx] = useState<number>(0)
  const [themeColor, setThemeColor] = useState<string>("#1457A6")

  // Reset product selection when state changes
  useEffect(() => {
    setSelectedProductIdx(0)
  }, [selectedStateId])

  const activeProduct = activeState.topProducts[selectedProductIdx] || activeState.topProducts[0]

  const activeProductSeries = useMemo(() => {
    return {
      title: activeProduct.name,
      currentValue: activeProduct.metricValue,
      growthPercent: activeProduct.growthPercent,
      points: activeProduct.points,
    }
  }, [activeProduct])

  const maxProductShare = useMemo(() => {
    return Math.max(...activeState.topProducts.map((p) => p.sharePercent), 1)
  }, [activeState])

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)
  const markersRef = useRef<L.Marker[]>([])
  const circlesRef = useRef<L.Circle[]>([])

  // Initialize proper geographic Leaflet map of India
  useEffect(() => {
    if (!mapContainerRef.current) return

    if (!mapInstanceRef.current) {
      const map = L.map(mapContainerRef.current, {
        center: [22.8, 79.5],
        zoom: 4.4,
        minZoom: 3.8,
        maxZoom: 7,
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: false,
        dragging: true,
        doubleClickZoom: false,
      })

      // Clean, English-only World Light Gray Basemap
      L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
        {
          maxZoom: 16,
          attribution: "Tiles &copy; Esri",
        }
      ).addTo(map)

      // Reference labels layer
      L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
        { maxZoom: 16 }
      ).addTo(map)

      mapInstanceRef.current = map
    }

    const map = mapInstanceRef.current

    // Clear previous markers & circles
    markersRef.current.forEach((m) => m.remove())
    markersRef.current = []
    circlesRef.current.forEach((c) => c.remove())
    circlesRef.current = []

    // Render demand colored circles for clusters using dynamic theme primary color
    STATE_DATA.forEach((state) => {
      const isSelected = state.id === selectedStateId
      const circle = L.circle(state.coordinates, {
        radius: state.demandZoneRadiusMeters,
        color: themeColor,
        weight: isSelected ? 1.5 : 0.75,
        fillColor: themeColor,
        fillOpacity: isSelected ? 0.16 : 0.05,
      }).addTo(map)
      circlesRef.current.push(circle)
    })

    // Render interactive markers
    STATE_DATA.forEach((state) => {
      const isSelected = state.id === selectedStateId
      
      const iconHtml = isSelected
        ? `
        <div 
          tabindex="0"
          role="button"
          aria-label="${state.stateName}, ${state.hubName}, ${state.suppliers} suppliers"
          style="
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 1.25px;
            border-radius: 11px;
            overflow: hidden;
            box-shadow: 0 4px 14px color-mix(in srgb, var(--primary) 25%, transparent);
            cursor: pointer;
            transform: scale(1.06);
            transition: transform 0.2s ease;
          ">
          <!-- Circulating Gradient Border -->
          <div style="
            position: absolute;
            inset: -250%;
            background: conic-gradient(from 0deg, var(--primary) 0%, var(--primary-container) 35%, var(--secondary) 70%, var(--primary) 100%);
            animation: spinCirculatingGradient 2.6s linear infinite;
          "></div>
          
          <!-- Inner White Pill -->
          <div style="
            position: relative;
            z-index: 1;
            display: flex;
            align-items: center;
            gap: 7px;
            background: #ffffff;
            color: #0f172a;
            border-radius: 9.75px;
            padding: 4px 10px;
            font-family: inherit;
            transition: transform 0.15s ease, box-shadow 0.15s ease;
          ">
            <span style="
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 8px;
              height: 8px;
              flex-shrink: 0;
            ">
              <span style="
                position: absolute;
                width: 100%;
                height: 100%;
                border-radius: 50%;
                background-color: var(--primary);
                animation: pingPulseDot 1.6s cubic-bezier(0, 0, 0.2, 1) infinite;
              "></span>
              <span style="
                position: relative;
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background-color: var(--primary);
              "></span>
            </span>
            <div style="text-align: left; line-height: 1.15;">
              <div style="font-size: 11px; font-weight: 700; color: #0f172a;">${state.hubName}</div>
              <div style="font-size: 8.5px; font-weight: 600; color: #64748b;">${state.suppliers} suppliers</div>
            </div>
          </div>
        </div>
      `
        : `
        <div 
          tabindex="0"
          role="button"
          aria-label="${state.stateName}, ${state.hubName}, ${state.suppliers} suppliers"
          style="
            display: inline-flex;
            align-items: center;
            gap: 7px;
            background: #ffffff;
            color: #1e293b;
            border: 1px solid rgba(203, 213, 225, 0.9);
            border-radius: 10px;
            padding: 4px 10px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            cursor: pointer;
            white-space: nowrap;
            font-family: inherit;
            transition: transform 0.15s ease, box-shadow 0.15s ease;
          ">
          <span style="
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background-color: var(--primary);
            display: inline-block;
            flex-shrink: 0;
          "></span>
          <div style="text-align: left; line-height: 1.15;">
            <div style="font-size: 11px; font-weight: 700; color: #0f172a;">${state.hubName}</div>
            <div style="font-size: 8.5px; font-weight: 600; color: #64748b;">${state.suppliers} suppliers</div>
          </div>
        </div>
      `

      const customIcon = L.divIcon({
        className: "custom-hub-pill",
        html: iconHtml,
        iconSize: [126, 40],
        iconAnchor: [63, 20],
      })

      const marker = L.marker(state.coordinates, { 
        icon: customIcon,
        zIndexOffset: isSelected ? 1000 : 0
      }).addTo(map)

      // Hover Tooltip: State, Hub, Verified suppliers, Demand level
      const tooltipContent = `
        <div style="font-family: system-ui, sans-serif; font-size: 11px; line-height: 1.35; padding: 3px 6px;">
          <div style="font-weight: 700; color: #0f172a; font-size: 12px;">${state.stateName}</div>
          <div style="color: var(--primary); font-weight: 700; margin-top: 3px;">${state.suppliers} verified suppliers</div>
        </div>
      `
      marker.bindTooltip(tooltipContent, {
        direction: "top",
        offset: [0, -18],
        className: "custom-hub-tooltip",
        opacity: 0.98,
      })

      marker.on("click", () => {
        setSelectedStateId(state.id)
      })

      markersRef.current.push(marker)
    })

    return () => {}
  }, [selectedStateId, themeColor])

  return (


    <div id="global-corridor" className="w-full scroll-mt-16 sm:scroll-mt-20">
      {/* 2-Column Grid */}
        <div id="pan-india-network" className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5 items-stretch scroll-mt-16 sm:scroll-mt-20">
          
          {/* Left Column: Interactive India Map (Span 6) */}
          <div className="lg:col-span-6 card-luminous bg-white dark:bg-slate-900 !border-slate-200/80 dark:!border-slate-800 rounded-2xl p-2.5 sm:p-3 shadow-xs relative overflow-hidden flex flex-col h-[330px] sm:h-[360px] lg:h-[385px]">
            
            {/* Map Canvas */}
            <div className="relative w-full h-full flex-grow rounded-xl overflow-hidden border border-slate-200/60 dark:border-slate-800 z-0">
              <div ref={mapContainerRef} className="w-full h-full select-none" />
            </div>
          </div>

          {/* Right Column: Selected State Intelligence Panel (Span 6) */}
          <div className="lg:col-span-6 card-luminous bg-white dark:bg-slate-900 !border-slate-200/80 dark:!border-slate-800 rounded-2xl p-3 sm:p-3.5 shadow-xs flex flex-col justify-between h-auto lg:h-[385px] overflow-hidden">
            
            {/* Header with State Icon and Title */}
            <div className="flex items-center gap-2 pb-2.5 border-b border-slate-100 dark:border-slate-800">
              <MapPin className="size-3.5 sm:size-4 text-primary shrink-0" />
              <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 min-w-0">
                <h3 className="font-bold text-sm sm:text-base text-slate-900 dark:text-white tracking-tight leading-none">
                  {activeState.stateName}
                </h3>
                <span className="text-[11.5px] sm:text-xs text-slate-500 dark:text-slate-400">
                  {activeState.hubName} · Manufacturing Hub
                </span>
              </div>
            </div>

            {/* 2-Column Market Snapshot: Left Leaderboard (50%) & Right Live Benchmark Graph (50%) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-3.5 items-stretch flex-1 min-h-0 py-2 my-auto">
              
              {/* Left: Top 5 Product Leaderboard */}
              <div className="flex flex-col justify-between overflow-hidden">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate text-[0.7rem] sm:text-[0.72rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                      Top 5 Products
                    </span>
                    <a
                      href={TRADEMAP_SOURCE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-[0.62rem] text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
                      title="Data Source: Trade Map"
                    >
                      <span>Source</span>
                      <ExternalLink size={9} className="opacity-70" />
                    </a>
                  </div>
                  <div className="flex shrink-0 px-1.5 py-0.5 text-[0.62rem] sm:text-[0.65rem] font-semibold text-primary">
                    Volume Share
                  </div>
                </div>

                <div className="flex flex-1 flex-col justify-between min-h-0 gap-1 overflow-y-auto no-scrollbar">
                  {activeState.topProducts.map((p, i) => {
                    const rank = i + 1
                    const active = selectedProductIdx === i
                    const isPositive = p.growthPercent >= 0
                    const pctWidth = maxProductShare > 0 ? Math.max(14, Math.min(100, Math.round((p.sharePercent / maxProductShare) * 100))) : 40

                    return (
                      <button
                        key={p.name}
                        type="button"
                        onClick={() => setSelectedProductIdx(i)}
                        className={`group flex items-center gap-2 rounded-lg px-2 py-1 text-left transition-all ${
                          active ? "bg-primary/10 shadow-xs" : "hover:bg-accent/40"
                        }`}
                      >
                        <span className={`w-3.5 text-[0.7rem] sm:text-[0.72rem] font-bold ${active ? "text-primary" : "text-muted-foreground"}`}>
                          {rank}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-1">
                            <span className={`truncate text-[0.74rem] sm:text-[0.76rem] font-semibold ${active ? "text-primary" : "text-foreground"}`}>
                              {p.name}
                            </span>
                            <div className="flex shrink-0 items-baseline gap-1 text-[0.68rem] sm:text-[0.7rem] font-medium tabular-nums">
                              <span className={active ? "text-primary font-bold" : "text-muted-foreground font-semibold"}>
                                {p.sharePercent}%
                              </span>
                              <span
                                className="text-[0.64rem] sm:text-[0.66rem] font-semibold"
                                style={{
                                  color: isPositive
                                    ? "var(--graph-spark-pos, #059669)"
                                    : "var(--graph-spark-neg, #e11d48)"
                                }}
                              >
                                {isPositive ? `+${p.growthPercent.toFixed(1)}%` : `${p.growthPercent.toFixed(1)}%`}
                              </span>
                            </div>
                          </div>
                          <div className="mt-0.5 h-1 w-full overflow-hidden rounded-full bg-muted/60">
                            <div
                              className="h-full rounded-full transition-all duration-300"
                              style={{
                                width: `${pctWidth}%`,
                                background: active
                                  ? "linear-gradient(90deg, var(--graph-demand-from, var(--primary)) 0%, var(--graph-demand-to, #ff3355) 100%)"
                                  : "var(--graph-demand-from, var(--primary))",
                                opacity: active ? 1 : 0.45,
                                boxShadow: active ? "0 0 6px var(--graph-demand-glow, rgba(190,0,30,0.25))" : "none"
                              }}
                            />
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Right: Product Snapshot Graph */}
              <div className="h-[175px] sm:h-[195px] md:h-full flex flex-col">
                <ProductSnapshotGraph className="h-full" series={activeProductSeries} />
              </div>

            </div>

            {/* Bottom CTA Button */}
            <div className="pt-1">
              <Link
                to="/explore/suppliers"
                search={{ searchTerm: activeProduct ? activeProduct.name : activeState.stateName }}
                className="w-full flex items-center justify-center gap-2 py-2 px-3.5 rounded-xl bg-primary hover:bg-primary-hover text-xs font-bold text-primary-foreground transition-colors shadow-xs group"
              >
                <span>
                  {activeProduct
                    ? `Explore ${activeProduct.name} Suppliers`
                    : `Explore ${activeState.stateName} Suppliers`}
                </span>
                <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

          </div>

        </div>

      </div>

  )
}

